import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_FIREWALL_NETWORK } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import {
  FirewallNetworkAdapterItem,
  FirewallNetworkItem,
  ModuleId,
  SectionId,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  FirewallNetworkFormValue,
  FirewallNetworkSearchableKeywords,
} from '../../../../utils/uiSettings/firewall/firewall.types';
import {
  FIREWALL_NETWORK_DEFAULT_VALUE,
  getFirewallNetworkSearchableKeywords,
} from '../../../../utils/uiSettings/firewall/firewall.values';
import { GzFirewallNetworkTableWebComponent } from '../gridComponents/networks/GzFirewallNetworkTableWebComponent';
import { GzFirewallAdaptersTableWebComponent } from '../gridComponents/adapters/GzFirewallAdaptersTableWebComponent';
import { GzDataTableWebComponent } from '../../../../../../../ui/gzDataTable';

@customElement(WEB_COMPONENT_POLICY_PANEL_FIREWALL_NETWORK)
export class GzFirewallNetworkWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  FirewallNetworkFormValue,
  FirewallNetworkSearchableKeywords
> {
  /**
   * Path for Firewall - Settings section.
   */
  protected path = {
    moduleId: ModuleId.FIREWALL,
    sectionId: SectionId.FIREWALL_NETWORK,
  };

  /**
   * Searchable keywords for Firewall - Settings section.
   */
  protected searchableKeywords = getFirewallNetworkSearchableKeywords();

  @query('#networks-items-form-field')
  private networksItemsFormField: GzFormFieldWebComponent<GzFirewallNetworkTableWebComponent, FirewallNetworkItem[]>;

  @query('#adapters-items-form-field')
  private adaptersItemsFormField: GzFormFieldWebComponent<
    GzFirewallAdaptersTableWebComponent,
    FirewallNetworkAdapterItem[]
  >;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-title
      class="content"
      searchable-label="${this.searchableKeywords[FirewallNetworkSearchableKeywords.Settings].label}"
      ><span>${this.searchableKeywords[FirewallNetworkSearchableKeywords.Settings].translatedLabel}</span>
    </gz-title>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text fontSize="12" fontWeight="400" class="infoText"
      >${translateFn(
        'Firewall applies network profiles based on trust levels, customizable for different networks and adapters. '
      )}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    this.updateComplete.then(() => {
      const adaptersDataTable = this.adaptersItemsFormField.Input.shadowRoot?.querySelector(
        'gz-data-table'
      ) as GzDataTableWebComponent;
      if (adaptersDataTable) {
        adaptersDataTable.style.zIndex = '0';
      }
    });

    return html`
      <gz-title
        id="firewall-networks-title"
        fontSize="11"
        searchable-label="${this.searchableKeywords[FirewallNetworkSearchableKeywords.Networks].label}"
        fontWeight="600"
        class="form-subtitle"
        >${this.searchableKeywords[FirewallNetworkSearchableKeywords.Networks].translatedLabel}</gz-title
      >

      <gz-text id="firewall-networks-text" fontSize="12" fontWeight="400" class="infoText pt-1"
        >${translateFn(' Define profiles for existing networks and sub-networks in your company.')}
      </gz-text>

      <gz-form-field id="networks-items-form-field">
        <gz-policy-panel-firewall-network-table gz-form-input></gz-policy-panel-firewall-network-table>
      </gz-form-field>

      <gz-title
        id="firewall-adapters-title"
        fontSize="11"
        searchable-label="${this.searchableKeywords[FirewallNetworkSearchableKeywords.Adapters].label}"
        fontWeight="600"
        class="form-subtitle"
        >${this.searchableKeywords[FirewallNetworkSearchableKeywords.Adapters].translatedLabel}</gz-title
      >

      <gz-text id="firewall-adapters-text" fontSize="12" fontWeight="400" class="infoText pt-1"
        >${translateFn(
          "For undefined networks, firewall settings vary based on the network adapter used. Use existing profiles to filter network traffic and select the endpoint's network discovery behavior, whether on the local network or the internet. Network profiles and network discovery operate independently."
        )}
      </gz-text>

      <gz-form-field
        id="adapters-items-form-field"
        searchable-label="${this.searchableKeywords[FirewallNetworkSearchableKeywords.Wired].label} ${this
          .searchableKeywords[FirewallNetworkSearchableKeywords.Wireless].label} ${this.searchableKeywords[
          FirewallNetworkSearchableKeywords.Virtual
        ].label}"
        label=""
      >
        <gz-policy-panel-firewall-adapters-table gz-form-input></gz-policy-panel-firewall-adapters-table>
      </gz-form-field>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.firewall.network
   */
  protected getValueFromManager(): FirewallNetworkFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [ModuleId.FIREWALL]: { [SectionId.FIREWALL_NETWORK]: FIREWALL_NETWORK_DEFAULT_VALUE },
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [ModuleId.FIREWALL]: {
          [SectionId.FIREWALL_NETWORK]: PolicyPanelManager.getInstance().getPolicy('uiSettings.firewall.network'),
        },
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        firewall: {
          network: {
            adapters: this.adaptersItemsFormField,
            networks: this.networksItemsFormField,
          },
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: this.valueT.uiSettings,
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);
    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'firewall', 'network'], {});
    }

    return value;
  }

  /**
   * Override reset method, because we need to reset also grid component.
   */
  public reset() {
    super.reset();
    this.networksItemsFormField.Input.reset();
    this.adaptersItemsFormField.Input.reset();
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_FIREWALL_NETWORK: GzFirewallNetworkWebComponent;
  }
}
